import {
  Box,
  Header,
  Popover,
  SpaceBetween,
  StatusIndicator,
  StatusIndicatorProps,
} from '@cloudscape-design/components';

import { PlacePeriodReport, Task } from '../common/types';
import { useMemo } from 'react';
import { formatDateStringWithTime } from '../common/formatting/dateFormat';
import { computeHasGooglePeriods, computeStatus } from '../common/cardLogic';

export const MAX_RETRY_ATTEMPTS = 3;

export const TASK_STATUS_MAP: Record<string, StatusIndicatorProps.Type> = {
  pending: 'pending',
  'in-progress': 'in-progress',
  completed: 'success',
  failed: 'warning',
};

export const TASK_STATUS_INFO_MAP: Record<string, string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

export const TASK_NAME_MAP: Record<string, string> = {
  collect_details: 'Collecting Details',
  collect_details_source: 'Collecting Details',
  collect_maps: 'Collecting Maps',
  collect_reviews: 'Collecting Reviews',
  collect_reviews_source: 'Collecting Reviews',
  extract_contacts: 'Extracting Contacts',
  extract_report_issues: 'Analyzing Issues',
  organize_contacts: 'Organizing Contacts',
  collect_web_contacts: 'Crawling Website',
  report_statistics: 'Calculating Statistics',
  report_statistics_source: 'Calculating Statistics',
  upload_drip_contacts: 'Processing Drip Contacts',
};

export const FAILED_TASK_NAME_MAP: Record<string, string> = {
  collect_details: 'Failed to Collect Details',
  collect_details_source: 'Failed to Collect Details',
  collect_maps: 'Failed to Collect Maps',
  collect_reviews: 'Failed to Collect Reviews',
  collect_reviews_source: 'Failed to Collect Reviews',
  extract_contacts: 'Failed to Extract Contacts',
  extract_report_issues: 'Failed to Analyze Issues',
  organize_contacts: 'Failed to Organize Contacts',
  collect_web_contacts: 'Failed to Crawl Website',
  report_statistics: 'Failed to Calculate Statistics',
  report_statistics_source: 'Failed to Calculate Statistics',
  upload_drip_contacts: 'Failed to Process Drip Contacts',
};

export const DONE_TASK_NAME_MAP: Record<string, string> = {
  collect_details: 'Collected Details',
  collect_details_source: 'Collected Details',
  collect_maps: 'Collected Maps',
  collect_reviews: 'Collected Reviews',
  collect_reviews_source: 'Collected Reviews',
  extract_contacts: 'Extracted Contacts',
  extract_report_issues: 'Analyzed Issues',
  organize_contacts: 'Organized Contacts',
  collect_web_contacts: 'Crawled Website',
  report_statistics: 'Calculated Statistics',
  report_statistics_source: 'Calculated Statistics',
  upload_drip_contacts: 'Processed Drip Contacts',
};

export const TASK_DETAILED_INFO_MAP: Record<string, string> = {
  collect_details: 'Collecting details from Google Maps...',
  collect_details_source: 'Collecting details from different sources...',
  collect_maps: 'Collecting map information from Google Maps...',
  collect_reviews: 'Collecting reviews from Google Maps...',
  collect_reviews_source: 'Collecting reviews from multiple sources...',
  extract_contacts: 'Extracting contact information from reviews...',
  extract_report_issues: 'Analyzing reviews for common issues...',
  organize_contacts: 'Organizing contact information...',
  collect_web_contacts: 'Crawling website for contact information...',
  report_statistics: 'Calculating scorecard statistics...',
  report_statistics_source: 'Calculating scorecard statistics...',
  upload_drip_contacts: 'Processing drip contacts...',
};

export const FAILED_TASK_DETAILED_INFO_MAP: Record<string, string> = {
  collect_details: 'Failed to collect details from Google Maps.',
  collect_details_source: 'Failed to collect details from different sources.',
  collect_maps: 'Failed to collect map information from Google Maps.',
  collect_reviews: 'Failed to collect reviews from Google Maps.',
  collect_reviews_source: 'Failed to collect reviews.',
  extract_contacts: 'Failed to extract contact information from reviews.',
  extract_report_issues: 'Failed to analyze issues.',
  organize_contacts: 'Failed to organize contact information.',
  collect_web_contacts: 'Failed to crawl website for contact information.',
  report_statistics: 'Failed to calculate scorecard statistics.',
  report_statistics_source: 'Failed to calculate scorecard statistics.',
  upload_drip_contacts: 'Failed to process drip contacts.',
};

export const DONE_TASK_DETAILED_INFO_MAP: Record<string, string> = {
  collect_details: 'Collected details from Google Maps.',
  collect_details_source: 'Collected details from different sources.',
  collect_maps: 'Collected map information from Google Maps.',
  collect_reviews: 'Collected reviews from Google Maps.',
  collect_reviews_source: 'Collected reviews from multiple sources.',
  extract_contacts: 'Extracted contact information from reviews.',
  extract_report_issues: 'Analyzed common issues from reviews.',
  organize_contacts: 'Organized contact information.',
  collect_web_contacts: 'Crawled website for contact information.',
  report_statistics: 'Calculated scorecard statistics.',
  report_statistics_source: 'Calculated scorecard statistics.',
  upload_drip_contacts: 'Processed drip contacts.',
};

export function PlaceStatus({
  tasks,
  text,
  periods,
}: {
  tasks?: Task[];
  text?: string;
  periods?: PlacePeriodReport[];
}) {
  // Get the most recent task and its status
  // const mostRecentTask = tasks.sort(
  //   (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  // );

  const sortedTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    const sortedTasks = [...tasks];
    sortedTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return sortedTasks;
  }, [tasks]);

  const mostRecentTask = sortedTasks[0];

  const task =
    sortedTasks.length > 0
      ? mostRecentTask
      : ({
          status: 'pending',
          name: 'collect_details',
          environment: null,
          retries: null,
          created_at: new Date().toISOString(),
        } as Task);

  // 'pending' | 'in-progress' | 'completed' | 'failed' -> 'error' | 'warning' | 'success' | 'info' | 'stopped' | 'pending' | 'in-progress' | 'loading'
  // show pending, in-progress, or failed if the task is not the last task along with the task name
  // show pending, in-progress, completed, or failed if the task is the last task (or if it hasn't been updated in the last 5 minutes) along with the task name

  const status = task.status;
  const name = task.name;
  let nameMap = TASK_NAME_MAP;
  let infoMap = TASK_DETAILED_INFO_MAP;
  let detailedInfoMap = TASK_DETAILED_INFO_MAP;
  switch (status) {
    case 'failed':
      nameMap = FAILED_TASK_NAME_MAP;
      infoMap = FAILED_TASK_DETAILED_INFO_MAP;
      detailedInfoMap = FAILED_TASK_DETAILED_INFO_MAP;
      break;
    case 'completed':
      nameMap = DONE_TASK_NAME_MAP;
      infoMap = DONE_TASK_DETAILED_INFO_MAP;
      detailedInfoMap = DONE_TASK_DETAILED_INFO_MAP;
      break;
    default:
      break;
  }
  return (
    <Popover
      size="large"
      triggerType="text"
      content={
        <SpaceBetween direction="vertical" size="xxs">
          <Header
            variant="h3"
            description={
              <SpaceBetween direction="vertical" size="xxs">
                {formatDateStringWithTime(task.created_at)}
                {infoMap[name]}
              </SpaceBetween>
            }
            counter={
              status === 'failed' ? `(${task.retries ?? 0}/${MAX_RETRY_ATTEMPTS})` : undefined
            }
          >
            {nameMap[name]}
          </Header>
          {status === 'failed' && (
            <SpaceBetween direction="vertical" size="xxs">
              {task.retries && task.retries > 0 && task.retries < MAX_RETRY_ATTEMPTS ? (
                <Box variant="span">Retrying...</Box>
              ) : (
                <Box variant="span">
                  Failed to complete task and will not retry. Please check the logs for more
                  information.
                </Box>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      }
    >
      <StatusIndicator
        type={
          periods === undefined
            ? TASK_STATUS_MAP[task.status]
            : computeStatus(tasks ?? [], computeHasGooglePeriods(periods))
        }
        wrapText={false}
      >
        {text ? text : TASK_STATUS_INFO_MAP[task.status]}
      </StatusIndicator>
    </Popover>
  );
}
